import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription, finalize, timer } from 'rxjs';
import { DataService } from 'src/app/helpers/data.service';
import { TasksService } from '../tasks.service';

@Component({
  selector: 'task-time-tracking',
  templateUrl: './time-tracking.component.html',
  styleUrls: ['./time-tracking.component.scss'],
})
export class TimeTrackingComponent implements OnDestroy {
  @Input() runningTaskId: string | null = null;
  @Output() runningTaskChanged = new EventEmitter<string | null>();

  protected subscription!: Subscription;
  protected time: string = '00:00:00';

  form: FormGroup;

  private _emp: any;
  private _tasks: any[] = [];
  private _records: string[] = [];
  private _pRecords: any;
  private _started: string | null = null;
  private _startTimeMs: number | null = null;
  private _loading = false;
  private _error = false;

  constructor(
    private _dataServ: DataService,
    private _taskServ: TasksService,
    fb: FormBuilder,
    private _toastrServ: ToastrService,
    private ch: ChangeDetectorRef
  ) {
    this._emp = _dataServ.employeeValue;

    this.form = fb.nonNullable.group({
      id: [null],
      timeIn: [null],
      timeOut: [null],
      status: ['start'],
      taskId: [''],
      task: [''],
      employeeId: [this._emp?.id],
    });

    this.initTimerListener();
    this.initTaskListener();
    this.GetAllRecords();
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  get id() { return this.form.get('id'); }
  get timeIn() { return this.form.get('timeIn'); }
  get timeOut() { return this.form.get('timeOut'); }
  get status() { return this.form.get('status'); }
  get taskId() { return this.form.get('taskId'); }
  get task() { return this.form.get('task'); }
  get employeeId() { return this.form.get('employeeId'); }

  get tasks(): any[] { return this._tasks; }
  set tasks(v: any[]) { this._tasks = v; }

  protected get records(): string[] {
    return [...this._records].reverse();
  }
  protected set records(v: string[]) {
    this._records = v;
  }

  get pRecords(): any {
    return this._pRecords;
  }
  set pRecords(v: any) {
    const updated = { ...v };
    this.records = Object.keys(updated);

    this.records.forEach(k => {
      if (typeof updated[k].time === 'number') {
        updated[k].time = this.msToTime(updated[k].time);
      }

      updated[k].arr.forEach((x: any) => {
        if (x.status !== 'start') {
          x.time = this.msToTime(x.time);
        }
      });
    });

    this._pRecords = updated;
  }

  get started(): string | null { return this._started; }
  set started(v: string | null) { this._started = v; }

  get loading(): boolean { return this._loading; }
  set loading(v: boolean) { this._loading = v; }

  get error(): boolean { return this._error; }
  set error(v: boolean) { this._error = v; }

  private initTimerListener() {
    this.timeIn?.valueChanges.subscribe((value: Date | string | null) => {
      this.clearTimer();

      const startedAt = this.parseDate(value);

      if (!startedAt) {
        this._startTimeMs = null;
        this.time = '00:00:00';
        this.ch.markForCheck();
        return;
      }

      this._startTimeMs = startedAt.getTime();

      const updateElapsedTime = () => {
        if (this._startTimeMs === null) {
          this.time = '00:00:00';
          return;
        }

        this.time = this.msToTime(Math.max(0, Date.now() - this._startTimeMs));
        this.ch.markForCheck();
      };

      updateElapsedTime();
      this.subscription = timer(1000, 1000).subscribe(updateElapsedTime);
    });
  }

  private clearTimer() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null!;
    }
  }

  msToTime(duration: number): string {
    const seconds = Math.floor((duration / 1000) % 60);
    const minutes = Math.floor((duration / (1000 * 60)) % 60);
    const hours = Math.floor(duration / (1000 * 60 * 60));

    return `${hours.toString().padStart(2, '0')}:` +
      `${minutes.toString().padStart(2, '0')}:` +
      `${seconds.toString().padStart(2, '0')}`;
  }

  private parseDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private sortRecords(records: any[]): any[] {
    return [...records].sort(
      (a, b) =>
        new Date(a.createdDate ?? a.timeIn).getTime() -
        new Date(b.createdDate ?? b.timeIn).getTime()
    );
  }

  private GetAllRecords() {
    this.loading = true;

    this._taskServ
      .GetTimeRecordsByEmpAndStatus(this._emp.id)
      .pipe(finalize(() => this.GetTasks()))
      .subscribe({
        next: res => {
          const sortedRecords = this.sortRecords(res);

          const last = sortedRecords[sortedRecords.length - 1];

          if (last?.status === 'start') {
            this.started = last.taskId;
            this.taskId?.setValue(last.taskId);
            this.id?.setValue(last.id);
            this.timeIn?.setValue(new Date(last.timeIn));
            this._dataServ.setRunningTaskId(last.taskId);
            this.runningTaskChanged.emit(last.taskId);
          } else {
            this.runningTaskChanged.emit(null);
          }

          this.pRecords = this.ProcessRecords(sortedRecords);
        },
        error: () =>
          this._toastrServ.error(
            'Something went wrong while fetching time records'
          ),
      });
  }

  private GetTasks() {
    this.loading = true;

    this._taskServ
      .GetTasksInProgressByEmployee(this._emp.id)
      .subscribe({
        next: res => {
          this.tasks = (res || []).filter((task: any) => {
            if (!task) return false;
            if (typeof task.status === 'string') {
              return task.status.toLowerCase().replace(/[\s-_]/g, '') === 'inprogress';
            }
            if (task.status && typeof task.status === 'object') {
              const statusName = (task.status.status || task.status.name || task.status.title || '')
                .toLowerCase()
                .replace(/[\s-_]/g, '');
              return statusName === '' || statusName === 'inprogress';
            }
            return true;
          });
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this._toastrServ.error(
            'Something went wrong while fetching tasks'
          );
        },
      });
  }

  private ProcessRecords(records: any[]) {
    return records.reduce((result, item) => {
      const key = item.taskId;
      const start = new Date(item.timeIn).getTime();

      if (!result[key]) {
        result[key] = { time: 0, arr: [] };
      }

      if (item.status !== 'start') {
        const end = new Date(item.timeOut).getTime();
        const diff = end - start;
        result[key].time += diff;
        item.time = diff;
      }

      result[key].arr.push(item);
      return result;
    }, {});
  }

  HandlePlay = (e: any, loading: any) => {
    loading(true);
    this.status?.setValue(e.status);

    if (e.status === 'start') {
      if (!e.id && !this.taskId?.value) {
        this.error = true;
        loading(false);
        return;
      }

      const taskId = e.id ?? this.taskId?.value;

      this._taskServ.GetTime().subscribe((res1: any) => {
        const currentTime = typeof res1 === 'string' ? res1 : String(res1);
        const startedAt = this.parseDate(currentTime);

        if (!startedAt) {
          loading(false);
          this._toastrServ.error('Something went wrong while starting timer');
          return;
        }

        const payload = {
          timeIn: currentTime,
          timeOut: null,
          status: 'start',
          taskId,
          employeeId: this._emp.id,
        };

        this._taskServ.AddTimeTracking(payload).subscribe(res => {
          const sortedRecords = this.sortRecords(res);
          const startedRecord = [...sortedRecords]
            .reverse()
            .find(x => x.status === 'start' && x.taskId === taskId);

          this.time = '00:00:00';
          this.id?.setValue(startedRecord?.id ?? null);
          this.taskId?.setValue(taskId);
          this.timeOut?.setValue(null);
          this.timeIn?.setValue(startedAt);
          this.started = taskId;
          this._dataServ.setRunningTaskId(taskId);
          this.runningTaskChanged.emit(taskId);
          this.pRecords = this.ProcessRecords(sortedRecords);
          loading(false);
        });
      });
    } else {
      const timeInLocal = this.toLocalISOString(this.timeIn!.value);

      this._taskServ.GetTime().subscribe((res1: any) => {
        const currentTime = typeof res1 === 'string' ? res1 : String(res1);
        const payload = {
          timeIn: timeInLocal,
          timeOut: currentTime,
          status: 'stop',
          taskId: e.id,
          employeeId: this._emp.id,
        };

        this._taskServ.AddTimeTracking(payload).subscribe(res => {
          this.clearTimer();
          this.form.reset({
            status: 'start',
            employeeId: this._emp.id,
          });
          this.time = '00:00:00';
          this.started = null;
          this._dataServ.setRunningTaskId(null);
          this.runningTaskChanged.emit(null);
          this.pRecords = this.ProcessRecords(this.sortRecords(res));
          loading(false);
        });
      });
    }
  };

  private initTaskListener() {
    this.taskId?.valueChanges.subscribe(v => {
      if (v) this.error = false;
    });
  }

  private toLocalISOString(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');

    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
}
